// Side menu drill-down. A row whose link has children opens a level panel: a back row
// (parent title, or the section heading at the first level), the current title as a
// heading, a bold "Xem tất cả …" link to the current collection, then one row per child.
// Levels stack and are rebuilt from the lean lists that mega-menu.liquid ships inside
// <template>, so the deep catalog never sits in the live DOM. The stack resets whenever
// the menu closes, like the design's SideMenu.
import { levelView, popLevel, pushLevel, resetLevels } from './mega-menu-stack.js';

const ROW = 'ptt-mega__row';

export const initMegaMenu = (menu) => {
  const body = menu.querySelector('[data-mega-body]');
  const root = menu.querySelector('[data-mega-root]');
  const level = menu.querySelector('[data-mega-level]');
  const icons = menu.querySelector('[data-mega-icons]')?.content;
  if (!body || !root || !level) return;

  const branches = new WeakMap();
  let stack = [];

  const icon = (name) => icons?.querySelector(`[data-icon="${name}"] svg`)?.cloneNode(true) || '';
  const label = (text) => Object.assign(document.createElement('span'), { textContent: text });
  const fill = (template, title) => (template || '').replace('[title]', title);
  const directChild = (node, tag) => Array.from(node.children).find((child) => child.localName === tag);

  // Children of a root row live in the <template> right after it; deeper rows keep the
  // nested <ul> they were built from.
  const branchOf = (row) => {
    if (branches.has(row)) return branches.get(row);
    const template = row.nextElementSibling;
    return template?.matches('template[data-mega-branch]') ? template.content.querySelector('ul') : null;
  };

  const childRow = (item) => {
    const source = directChild(item, 'a');
    const nested = directChild(item, 'ul');
    const row = document.createElement('a');
    row.className = ROW;
    row.href = source?.getAttribute('href') || '#';
    row.append(label(source?.textContent.trim() || ''));
    if (nested?.children.length) {
      row.dataset.megaDrill = '';
      row.setAttribute('role', 'button');
      row.setAttribute('aria-expanded', 'false');
      row.setAttribute('aria-controls', level.id);
      row.append(icon('next'));
      branches.set(row, nested);
    }
    const li = document.createElement('li');
    li.append(row);
    return li;
  };

  const renderLevel = () => {
    const view = levelView(stack);
    if (!view) {
      level.hidden = true;
      level.replaceChildren();
      root.hidden = false;
      return;
    }
    const back = document.createElement('button');
    back.type = 'button';
    back.className = `${ROW} ${ROW}--back`;
    back.dataset.megaBack = '';
    back.setAttribute('aria-label', fill(menu.dataset.backLabel, view.backTitle));
    back.append(icon('back'), label(view.backTitle));

    const heading = Object.assign(document.createElement('h2'), { className: 'ptt-mega__heading', textContent: view.title });
    const viewAll = Object.assign(document.createElement('a'), { className: `${ROW} ${ROW}--bold`, href: view.url });
    viewAll.append(label(fill(menu.dataset.viewAll, view.title)));
    const list = Object.assign(document.createElement('ul'), { className: 'ptt-mega__rows' });
    const first = document.createElement('li');
    first.append(viewAll);
    list.append(first, ...Array.from(view.list.children, childRow));

    level.replaceChildren(back, heading, list);
    root.hidden = true;
    level.hidden = false;
  };

  const levelRows = () => Array.from(level.querySelectorAll(`.ptt-mega__rows > li > .${ROW}`));

  // A new level starts at its top; the offset of the list being left rides on the stack
  // entry so going back can restore it.
  const push = (row, list) => {
    const inRoot = root.contains(row);
    stack = pushLevel(stack, {
      title: row.textContent.trim(),
      url: row.getAttribute('href'),
      list,
      section: inRoot ? row.closest('.ptt-mega__section')?.querySelector('.ptt-mega__heading')?.textContent.trim() : '',
      trigger: inRoot ? row : null,
      index: inRoot ? -1 : levelRows().indexOf(row),
      scrollTop: body.scrollTop
    });
    row.setAttribute('aria-expanded', 'true');
    renderLevel();
    body.scrollTop = 0;
    level.querySelector('[data-mega-back]')?.focus({ preventScroll: true });
  };

  // Going back puts the parent list where the shopper left it and returns focus to the row
  // that opened the level, so keyboard users keep their place. That focus may scroll: if the
  // row is still outside the visible body (the window shrank meanwhile), it is brought into
  // view instead of being focused off-screen.
  const pop = () => {
    const back = popLevel(stack);
    if (!back) return;
    stack = back.stack;
    renderLevel();
    body.scrollTop = back.scrollTop;
    back.focus.trigger?.setAttribute('aria-expanded', 'false');
    (back.focus.trigger || levelRows()[back.focus.index])?.focus();
  };

  const reset = () => {
    const next = resetLevels(stack);
    stack = next.stack;
    renderLevel();
    if (next.scrollTop !== null) body.scrollTop = next.scrollTop;
    root.querySelectorAll('[data-mega-drill]').forEach((row) => row.setAttribute('aria-expanded', 'false'));
  };

  // Upgrade root rows that have a branch: with scripts they open a level instead of
  // navigating, so they are announced as expandable buttons.
  root.querySelectorAll('[data-mega-drill]').forEach((row) => {
    if (!branchOf(row)) return;
    row.setAttribute('role', 'button');
    row.setAttribute('aria-expanded', 'false');
    row.setAttribute('aria-controls', level.id);
  });

  body.addEventListener('click', (event) => {
    if (event.target.closest('[data-mega-back]')) {
      pop();
      return;
    }
    const row = event.target.closest('[data-mega-drill]');
    const list = row && branchOf(row);
    if (!list) return;
    event.preventDefault();
    push(row, list);
  });

  body.addEventListener('keydown', (event) => {
    const row = event.target.closest?.(`.${ROW}`);
    if (!row) return;
    const { key } = event;
    if (key === 'ArrowRight' && row.matches('[data-mega-drill][aria-expanded]')) {
      event.preventDefault();
      row.click();
    } else if (key === 'ArrowLeft' && stack.length) {
      event.preventDefault();
      pop();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
      const rows = Array.from((stack.length ? level : root).querySelectorAll(`.${ROW}`));
      const index = rows.indexOf(row);
      const next = { Home: 0, End: rows.length - 1, ArrowDown: index + 1, ArrowUp: index - 1 }[key];
      if (rows[next]) {
        event.preventDefault();
        rows[next].focus();
      }
    }
  });

  menu.addEventListener('pttek:mega-menu-close', reset);
};

// Links that act as buttons (menu triggers, drill rows, close) also answer Space. Like a
// native button they act on keyup: acting on keydown would move focus to the new level's
// back button, which would then receive the keyup and immediately go back.
const spaceControl = (event) => {
  if (event.key !== ' ') return null;
  const control = event.target.closest?.('a[role="button"]');
  return control && (control.matches('[data-mega-menu-open]') || control.closest('[data-mega-menu]')) ? control : null;
};
export const installSpaceActivation = (doc) => {
  doc.addEventListener('keydown', (event) => {
    if (spaceControl(event)) event.preventDefault();
  });
  doc.addEventListener('keyup', (event) => {
    const control = spaceControl(event);
    if (!control) return;
    event.preventDefault();
    control.click();
  });
};

// Guarded so node tests can import the module and drive it against their own document.
if (typeof document !== 'undefined') {
  installSpaceActivation(document);
  const megaMenu = document.querySelector('[data-mega-menu]');
  if (megaMenu) initMegaMenu(megaMenu);
}
